import React, { useState, useEffect } from 'react';
import { ShieldCheck, Users, Eye, EyeOff, Crown, ArrowRight, Loader2, Lock } from 'lucide-react';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


export default function EmpresaPermissõesPage() {
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const { hasModule, PLAN_NAMES } = usePlanLimits();
    const hasAdvanced = hasModule('permissões_avançadas');

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        fetch(`${API}/client/users`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json()).then(data => { setColaboradores(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const roles = [
        { role: 'Empresa Admin', desc: 'Acesso total ao painel da empresa. Gerencia colaboradores, configurações e plano.', color: 'var(--sv-gold-primary)', level: 3 },
        { role: 'Gestor', desc: 'Pode ver dashboard, equipe, aprovar ajustes. Não altera configurações.', color: '#3B82F6', level: 2 },
        { role: 'Colaborador', desc: 'Acesso básico: ponto digital, histórico, solicitações.', color: '#10B981', level: 1 },
    ];

    const modules = [
        { key: 'ponto', name: 'Ponto Digital', defaultAccess: { admin: true, gestor: true, colab: true } },
        { key: 'histórico', name: 'Meu Histórico', defaultAccess: { admin: true, gestor: true, colab: true } },
        { key: 'solicitacoes', name: 'Solicitações', defaultAccess: { admin: true, gestor: true, colab: true } },
        { key: 'alertas', name: 'Alertas', defaultAccess: { admin: true, gestor: true, colab: true } },
        { key: 'copilot', name: 'Copilot IA', defaultAccess: { admin: true, gestor: true, colab: true } },
        { key: 'financeiro', name: 'Financeiro', defaultAccess: { admin: true, gestor: false, colab: false } },
        { key: 'performance', name: 'Performance', defaultAccess: { admin: true, gestor: true, colab: false } },
        { key: 'escalas', name: 'Escalas', defaultAccess: { admin: true, gestor: false, colab: false } },
    ];

    return (
        <div className="space-y-5" data-testid="empresa-permissões">
            {/* Níveis de Acesso */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Níveis de Acesso</h3>
                <div className="space-y-2">
                    {roles.map((p) => (
                        <div key={p.role} className="flex items-start gap-3 p-3.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${p.color}15` }}>
                                <ShieldCheck size={14} style={{ color: p.color }} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{p.role}</p>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${p.color}12`, color: p.color }}>Nível {p.level}</span>
                                </div>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{p.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Matriz de Acesso */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Matriz de Acesso por Módulo</h3>
                    {!hasAdvanced && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-gold-primary)' }}>
                            <Lock size={8} /> Pro
                        </span>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--muted)' }}>Módulo</th>
                                <th className="text-center py-2 px-3 font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Admin</th>
                                <th className="text-center py-2 px-3 font-medium" style={{ color: '#3B82F6' }}>Gestor</th>
                                <th className="text-center py-2 px-3 font-medium" style={{ color: '#10B981' }}>Colab.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {modules.map((m) => (
                                <tr key={m.key} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td className="py-2.5 pr-4" style={{ color: 'var(--text)' }}>{m.name}</td>
                                    {['admin', 'gestor', 'colab'].map(role => (
                                        <td key={role} className="text-center py-2.5 px-3">
                                            {m.defaultAccess[role] ? (
                                                <Eye size={13} className="inline" style={{ color: '#10B981' }} />
                                            ) : (
                                                <EyeOff size={13} className="inline" style={{ color: '#555' }} />
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Colaboradores */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Equipe ({colaboradores.length})</h3>
                {colaboradores.length === 0 ? (
                    <div className="text-center py-10">
                        <Users size={32} className="mx-auto mb-2" style={{ color: 'var(--muted)' }} />
                        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum colaborador cadastrado</p>
                        <p className="text-xs mt-1 mb-3" style={{ color: 'var(--muted)' }}>Cadastre colaboradores para gerenciar seus acessos</p>
                        <a href="/sistema/empresa/colaboradores" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-gold-primary)' }}>
                            Cadastrar <ArrowRight size={10} />
                        </a>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {colaboradores.map((c) => {
                            const isGestor = c.role === 'empresa_gestor' || c.role === 'gestor';
                            return (
                                <div key={c.user_id} className="rounded-lg p-3.5" style={{ background: 'var(--surface-2)' }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold" style={{ background: isGestor ? 'rgba(59,130,246,0.12)' : 'rgba(16,185,129,0.12)', color: isGestor ? '#3B82F6' : '#10B981' }}>
                                            {c.name?.charAt(0).toUpperCase() || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{c.name}</p>
                                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{c.email}</p>
                                        </div>
                                        <span className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ background: isGestor ? 'rgba(59,130,246,0.12)' : 'rgba(16,185,129,0.08)', color: isGestor ? '#3B82F6' : '#10B981' }}>
                                            {isGestor ? 'Gestor' : 'Colaborador'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Upgrade CTA */}
            {!hasAdvanced && (
                <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(139,92,246,0.06) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                    <div className="flex items-start gap-3">
                        <Crown size={16} style={{ color: 'var(--sv-gold-primary)' }} className="shrink-0 mt-0.5" />
                        <div>
                            <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Controle granular de permissões</p>
                            <p className="text-[11px] mt-0.5" style={{ color: 'var(--muted)' }}>Com o plano Pro, você pode ativar/desativar módulos individualmente para cada colaborador.</p>
                            <a href="/sistema/empresa/assinatura" className="inline-flex items-center gap-1 text-[10px] font-medium mt-2" style={{ color: 'var(--sv-gold-primary)' }}>
                                Fazer upgrade <ArrowRight size={10} />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
